import { dataFilterMatchModeOptions, findKeyByValue, useEnumLabel } from "../../../../Utils/Utils";
import { orderStatusesTranslation, Translate, TranslateOrderStatus } from "../../../../Utils/TranslationUtils";
import useApi from "../../../../Utils/BackendClient";
import { useContext, useMemo } from "react";
import useGetEnums from "../../../../Utils/EnumsUtils";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../../../Utils/ResizeUtils";

import DateRowFilterTemplate from "../../../UIElements/FilterTemplates/DateRowFilterTemplate";
import DropDownTemplate from "../../../UIElements/FilterTemplates/DropDownTemplate";
import EnumsContext from "../../../../context/enums-context";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { SplitButton } from "primereact/splitbutton";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";

import commonStyle from "../../CommonStyles.module.css";

const Orders = () => {

    const buttonRef = useRef();
    const ctx = useContext(EnumsContext);
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const toast = useRef();
    const { deleteOrder, fetchOrders } = useApi();

    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState(null);
    const [reloadData, setReloadData] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: "created_at",
        sortOrder: 1,
        filters: {
            status: { value: null, matchMode: 'contains' },
            client_id: { value: null, matchMode: 'contains' },
            date: { value: null, matchMode: 'btw' }
        }
    });
    const [tableFilters, setTableFilters] = useState({
        status: { value: null, matchMode: 'contains' },
        client_id: { value: null, matchMode: 'contains' },
        date: { value: null, matchMode: 'btw' }
    });

    const enumTypes = useMemo(() => ["clients"], []);

    useGetEnums(enumTypes);

    const getClientLabel = useEnumLabel(ctx.enums.clients.data);

    useEffect(() => {
        const fetchData = async () => {

            const response = await fetchOrders(lazyState);

            if (response.status === 200) {
                setTotalRecords(response.data.count);
                setOrders(response.data.results);
            }
            else {
                toast.current.show({ severity: "error", summary: "Помилка завантаження списку актів", detail: "Помилка сервера " + response.status, life: 3000 });

                setOrders([]);
            }
            setLoading(false);
        }

        fetchData();

    }, [lazyState, fetchOrders, reloadData]);

    const onPage = (event) => {
        event.page = event.page + 1;
        setLazyState(event);
    };

    const onSort = (event) => {
        event.page = lazyState.page;
        setLazyState(event);
    }

    const onFilter = (event) => {
        event.page = 1;
        setTableFilters(event.filters);
    }

    const filterApply = () => {
        const editedLazyState = lazyState;
        editedLazyState.filters = tableFilters;
        setLazyState(editedLazyState);
        setReloadData(!reloadData)
    }

    const clearFilter = (paramName, value) => {
        setLazyState(prevState => ({
            ...prevState,
            filters: {
                ...prevState.filters,
                [paramName]: {
                    ...prevState.filters[paramName],
                    value: value
                }
            }
        }));
    }

    const clearFilterFromChips = (paramName, value) => {
        clearFilter(paramName, value);
        setTableFilters(prevState => ({
            ...prevState,
            [paramName]: {
                value: value
            }
        }));
    }

    const formatFilterChips = () => {

        const getChangedValue = (filterKey, value) => {

            if (filterKey === "client_id") {
                return getClientLabel(value);
            }
            else if (filterKey === "status") {
                return TranslateOrderStatus(value);
            }
            else if (filterKey === "date") {
                let splittedValues = value.toString().split(',');
                for (let i = 0; i < splittedValues.length; i++) {
                    splittedValues[i] = new Date(splittedValues[i]).toLocaleDateString('ua-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
                }
                value = splittedValues.join('-');
            }

            return value;
        };

        return (
            <div className="flex flex-wrap gap-2">
                {Object.keys(lazyState.filters).map((filterKey) => {
                    const filter = lazyState.filters[filterKey];

                    if (filter.value && filter.value !== "") {
                        const translatedKey = filterKey === "client_id" ? "Клієнт" : Translate(filterKey);
                        const changedValue = getChangedValue(filterKey, filter.value);

                        return (
                            <Chip
                                key={filterKey}
                                label={`${translatedKey}: ${changedValue}`}
                                removable
                                onRemove={() => clearFilterFromChips(filterKey, null)}
                            />
                        );
                    }
                    return null;
                })}
            </div>
        );
    };

    const DeleteButtonHandler = async (rowData) => {

        const response = await deleteOrder(rowData.id);

        if (response.status === 204) {
            toast.current.show({ severity: "success", summary: 'Видалення', detail: 'Замовлення успішно видалено', life: 3000 });
        }
        else if (response.status === 404) {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Замовлення " + rowData.id + " не знайдено", life: 3000 });
        }
        else if (response.status === 405) {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Замовлення " + rowData.id + "не може бути видалено", life: 3000 });
        }
        else {
            toast.current.show({ severity: "error", summary: "Помилка видалення", detail: "Помилка сервера " + response.status, life: 3000 });
        }

        setReloadData(!reloadData);
    }

    const confirmDelete = (event, rowData) => {
        confirmDialog({
            group: 'headless',
            header: 'Підтвердження',
            message: 'Підтвердіть видалення замовлення',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: "Так",
            rejectLabel: "Hi",
            defaultFocus: 'accept',
            accept: () => { DeleteButtonHandler(rowData) }
        });
    }

    const NavigateToDetailsPage = (rowData) => {

        if (rowData?.id) {
            const route = '/orders/editorder/' + rowData.id;
            navigate(route, { state: { id: rowData.id } });
        }
        else {
            navigate("/orders/neworder");
        }

    }

    const mainToolbarRightTemplate = () => {
        return (<div className="flex flex-wrap gap-2">
            <Button label="Додати замовлення" severity="success" className={commonStyle.addButton} onClick={() => NavigateToDetailsPage()} />
        </div>)
    }

    const tableHeader = (
        <div className="flex justify-content-between align-items-center w-full">
            {formatFilterChips()}
        </div>
    );

    const handleMobileFilterChange = (key, value) => {
        setTableFilters((prevFilters) => ({
            ...prevFilters,
            [key]: { ...prevFilters[key], value: value }
        }));
    }

    const clearMobileFilters = () => {
        setTableFilters({
            status: { value: null, matchMode: 'contains' },
            client_id: { value: null, matchMode: 'contains' },
            date: { value: null, matchMode: 'btw' }
        });

        setLazyState(prevState => ({
            ...prevState,
            filters: {
                status: { value: null, matchMode: 'contains' },
                client_id: { value: null, matchMode: 'contains' },
                date: { value: null, matchMode: 'btw' }
            }
        }));
    }

    const mobileTableHeader = () => {
        return (
            showFilters && <div className="grid">
                <div className="col-12 mt-3">
                    <FloatLabel>
                        <Dropdown
                            value={tableFilters.client_id.value}
                            onChange={e => handleMobileFilterChange("client_id", e.value)}
                            options={ctx.enums.clients.data}
                            placeholder="Виберіть значення"
                            className="p-column-filter w-full"
                            showClear
                        />
                        <label>Клієнт</label>
                    </FloatLabel>
                </div>
                <div className="col-12 mt-3">
                    <FloatLabel>
                        <Dropdown
                            value={TranslateOrderStatus(tableFilters.status.value)}
                            onChange={e => handleMobileFilterChange("status", findKeyByValue(orderStatusesTranslation, e.value))}
                            options={Object.values(orderStatusesTranslation)}
                            placeholder="Виберіть значення"
                            className="p-column-filter w-full"
                            showClear
                        />
                        <label>Статус</label>
                    </FloatLabel>
                </div>
                <div className="col-12 mt-3">
                    <FloatLabel>
                        <Calendar
                            value={tableFilters.date.value}
                            onChange={
                                (e) => handleMobileFilterChange("date", e.value)
                            }
                            dateFormat="dd.mm.yy"
                            selectionMode="range"
                            className="p-column-filter w-full"
                            readOnlyInput
                            hideOnRangeSelection
                        />
                        <label>Дата</label>
                    </FloatLabel>
                </div>
                <div className="col-6 flex justify-content-center">
                    <Button severity="secondary" outlined style={{ width: "98%" }} onClick={() => clearMobileFilters()} label="Очистити фільтри" />
                </div>
                <div className="col-6 flex justify-content-center">
                    <Button severity="success" outlined style={{ width: "98%" }} onClick={() => filterApply()} label="Застосувати" />
                </div>
            </div>
        );
    }

    const formatAdditionalButtons = (rowData) => {
        let buttons = [];

        buttons.push({
            label: 'Видалити',
            icon: 'pi pi-trash',
            command: (event) => { confirmDelete(event, rowData) }
        });

        buttons = buttons.length > 0 ? buttons : null;
        return buttons
    }

    const actionBodyTemplate = (rowData) => {
        const buttons = formatAdditionalButtons(rowData);

        const buttonLabel = rowData.status !== "FINALIZED" ? "Редагувати" : "Деталі акту";
        const buttonIcon = rowData.status !== "FINALIZED" ? "pi pi-pencil" : "pi pi-eye";

        return (
            <>
                {buttons !== null ? (
                    <SplitButton
                        label={!isMobile ? buttonLabel : null}
                        icon={buttonIcon}
                        onClick={() => NavigateToDetailsPage(rowData)}
                        model={buttons}
                        text
                        ref={buttonRef} // Set the ref here
                    />
                ) : (
                    <Button
                        label={!isMobile ? buttonLabel : null}
                        icon={buttonIcon}
                        onClick={() => NavigateToDetailsPage(rowData)}
                        text
                    />
                )}
            </>
        );
    };

    const toolbarLeftTemplate = (
        isMobile && <Button icon={`${!showFilters ? "pi pi-filter" : "pi pi-filter-slash"}`} severity="secondary" outlined onClick={() => setShowFilters(!showFilters)} />
    );

    const mobileActBody = (rowData) => {
        return (
            <div className="flex justify-content-between flex-wrap">
                <div className="grid w-8">
                    <div className="col-12">
                        <div className="flex-column">
                            <div>Замовлення №{rowData.number}</div>
                            <div><i className="pi pi-clock" /> {rowData.date}</div>
                            <div><i className={"pi pi-check"} /> {TranslateOrderStatus(rowData.status)}</div>
                            <div><i className="pi pi-user" /> {rowData.client ?? "Клієнта не вказано"}</div>
                        </div>
                    </div>
                </div>
                <div className="flex justify-content-center align-items-center">
                    {actionBodyTemplate(rowData)}
                </div>
            </div>
        )
    };

    return (<>
        <Toast ref={toast} />
        <Toolbar start={toolbarLeftTemplate} end={mainToolbarRightTemplate} />
        {!isMobile && <DataTable value={orders} header={tableHeader} stripedRows lazy rows={lazyState.rows} paginator onPage={onPage} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} first={lazyState.first}
            onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder} onFilter={onFilter} filters={lazyState.filters}
            dataKey="id" loading={loading} emptyMessage="Немає замовлень" tableStyle={{ minWidth: '60rem' }} >
            <Column field="number" header="Номер" sortable style={{ width: '20%' }} />
            <Column field="date" header="Дата" sortable filter filterElement={DateRowFilterTemplate} onFilterApplyClick={filterApply} filterMatchModeOptions={dataFilterMatchModeOptions}
                    onFilterClear={() => clearFilter("date", null)} body={(rowData) => new Date(rowData.date).toLocaleDateString('ua-UA', { day: '2-digit', month: '2-digit', year: 'numeric'})} style={{ width: '20%' }} />
            <Column field="client_id" header="Клієнт" sortable filter filterElement={(options) => DropDownTemplate(options, ctx.enums.clients.data)} onFilterApplyClick={filterApply}
                onFilterClear={() => clearFilter("client_id", null)} showFilterMatchModes={false} body={rowData => getClientLabel(rowData.client_id)} style={{ width: '20%' }} />
            <Column field="status" header="Статус" sortable filter filterElement={(options) => DropDownTemplate(options, orderStatusesTranslation)} onFilterApplyClick={filterApply} 
                onFilterClear={() => clearFilter("status", null)} showFilterMatchModes={false} body={(rowData) => TranslateOrderStatus(rowData.status)} style={{ width: '20%' }} />
            <Column field="action" body={actionBodyTemplate} style={{ width: '20%' }} />
        </DataTable>}
        {isMobile && <DataTable value={orders} header={mobileTableHeader} stripedRows lazy rows={lazyState.rows} paginator onPage={onPage} onSort={onSort} sortField={lazyState.sortField} sortOrder={lazyState.sortOrder}
            first={lazyState.first} loading={loading} totalRecords={totalRecords} rowsPerPageOptions={[10, 20, 50]} paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
            dataKey="id" emptyMessage="Немає замовлень" >
            <Column field="act_number" headerStyle={{ display: 'none' }} body={mobileActBody} />
        </DataTable>}
        <ConfirmDialog group="headless" />
    </>)
}

export default Orders;